import React, { useState } from "react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { 
  User, 
  Mail, 
  Calendar, 
  Clock,
  Building2,
  Shield,
  Eye,
  Edit,
  Settings,
  UserX
} from "lucide-react";

const EnhancedTeamMemberRow = ({ 
  member, 
  userRoles = [], 
  roles = [],
  subsidiaries = [],
  isSelected = false,
  onSelect,
  onViewProfile,
  onEdit,
  onManageAccess,
  onDeactivate
}) => {
  const [expanded, setExpanded] = useState(false);
  
  // Get member's roles - first check if member.roles exists (from backend), otherwise use userRoles
  const memberRoles = member.roles || userRoles.filter(ur => ur.user_id === member.id);
  const roleNames = memberRoles
    .map(role => role.role_name || roles.find(r => r.id === role.role_id)?.name)
    .filter(Boolean);
  
  // Get member's subsidiaries - use member.subsidiaries if available (from backend enrichment)
  const memberSubsidiaries = member.subsidiaries 
    ? member.subsidiaries.map(sub => sub.subsidiary_name)
    : memberRoles
        .map(ur => ur.subsidiary_id)
        .filter(Boolean)
        .map(subId => subsidiaries.find(s => s.id === subId)?.name)
        .filter(Boolean);
  
  const uniqueSubsidiaries = [...new Set(memberSubsidiaries)];
  
  // Calculate last login (mock for now)
  const getLastLoginText = () => {
    if (!member.updated_at) return "Never";
    const lastUpdate = new Date(member.updated_at);
    const now = new Date();
    const diffHours = Math.floor((now - lastUpdate) / (1000 * 60 * 60));
    
    if (diffHours < 1) return "Just now";
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return lastUpdate.toLocaleDateString();
  };

  return (
    <div className="border rounded-lg hover:bg-gray-50 transition-colors">
      {/* Main Row */}
      <div className="p-4">
        <div className="flex items-start gap-4">
          {/* Checkbox */}
          <div className="pt-1">
            <Checkbox
              checked={isSelected}
              onCheckedChange={onSelect}
            />
          </div>

          {/* User Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span className="font-semibold text-gray-900">{member.full_name || "N/A"}</span>
                  <Badge variant="outline" className="text-xs">
                    #{member.id?.slice(0, 8) || "N/A"}
                  </Badge>
                </div>
                
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                  <Mail className="h-3 w-3" />
                  <span>{member.email}</span>
                </div>

                <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    <span>Joined: {new Date(member.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    <span>Last login: {getLastLoginText()}</span>
                  </div>
                </div>
              </div>

              {/* Status Badge */}
              <div className="flex-shrink-0">
                <Badge 
                  variant={member.is_active ? "default" : "secondary"}
                  className={member.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}
                >
                  {member.is_active ? "✅ Active" : "Inactive"}
                </Badge>
              </div>
            </div>

            {/* Compact Info Row */}
            <div className="mt-3 flex flex-wrap items-center gap-4">
              {/* Subsidiaries */}
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-600">Subsidiaries:</span>
                {uniqueSubsidiaries.length > 0 ? (
                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className="text-xs">
                      {uniqueSubsidiaries.length}
                    </Badge>
                    {uniqueSubsidiaries.length <= 2 ? (
                      <span className="text-xs text-gray-600">
                        ({uniqueSubsidiaries.join(", ")})
                      </span>
                    ) : (
                      <span className="text-xs text-gray-600">
                        ({uniqueSubsidiaries[0]}, +{uniqueSubsidiaries.length - 1} more)
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-gray-400">None assigned</span>
                )}
              </div>

              {/* Roles */}
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-600">Roles:</span>
                {roleNames.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {roleNames.slice(0, 2).map((roleName, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {roleName}
                      </Badge>
                    ))}
                    {roleNames.length > 2 && (
                      <Badge variant="outline" className="text-xs">
                        +{roleNames.length - 2}
                      </Badge>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-gray-400">No roles</span>
                )}
              </div>
            </div>

            {/* Expanded Details */}
            {expanded && (
              <div className="mt-4 pt-4 border-t space-y-3">
                <div>
                  <span className="text-sm font-medium text-gray-700">All Subsidiaries:</span>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {uniqueSubsidiaries.length > 0 ? (
                      uniqueSubsidiaries.map((subName, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {subName}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-sm text-gray-400">None assigned</span>
                    )}
                  </div>
                </div>

                <div>
                  <span className="text-sm font-medium text-gray-700">Permissions:</span>
                  <div className="mt-1 text-sm text-gray-600">
                    {roleNames.length > 0 ? (
                      <ul className="list-disc list-inside">
                        {roleNames.map((roleName, idx) => (
                          <li key={idx}>{roleName}</li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-gray-400">No permissions assigned</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setExpanded(!expanded);
              if (onViewProfile && !expanded) onViewProfile(member);
            }}
          >
            <Eye className="h-4 w-4 mr-2" />
            {expanded ? "Hide Details" : "View Profile"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(member)}
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onManageAccess(member)}
          >
            <Settings className="h-4 w-4 mr-2" />
            Manage Access
          </Button>
          {member.is_active && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDeactivate(member)}
              className="text-red-600 hover:text-red-700 hover:border-red-300"
            >
              <UserX className="h-4 w-4 mr-2" />
              Deactivate
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnhancedTeamMemberRow;
